import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const BookingPerformance = ({ dashboardData }) => {
  const { t } = useTranslation();

  const { series, options, stats } = useMemo(() => {
    const data = dashboardData?.performance14Days || {
      totalBooked: 0,
      totalRevenue: 0,
      averageDailyRate: 0,
      dailyData: [],
    };

    const categories = data.dailyData.map((d) => {
      const date = new Date(d.date);
      return date
        .toLocaleString("en", { month: "short", day: "2-digit" })
        .replace(",", "");
    });

    const revenueData = data.dailyData.map((d) => d.revenue || 0);

    return {
      stats: {
        totalBooked: data.totalBooked || 0,
        totalRevenue: data.totalRevenue || 0,
        adr: data.averageDailyRate || 0,
      },
      series: [
        {
          name: t("dashboard.revenue"),
          data: revenueData,
        },
      ],
      options: {
        chart: {
          type: "line",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        stroke: {
          curve: "smooth",
          width: 3,
          colors: ["#E8307A"],
        },
        fill: {
          type: "solid",
          opacity: 0.15,
          colors: ["#FFBCD2"],
        },
        markers: {
          size: 5,
          colors: ["#fff"],
          strokeColors: "#E8307A",
          strokeWidth: 2,
          hover: { size: 7 },
        },
        xaxis: {
          categories,
          labels: {
            style: { colors: "#64748B", fontSize: "11px" },
          },
          axisTicks: { show: false },
          axisBorder: { show: false },
        },
        yaxis: {
          min: 0,
          tickAmount: 5,
          labels: {
            formatter: (val) =>
              val >= 1000 ? `฿${(val / 1000).toFixed(1)}k` : `฿${val}`,
            style: { colors: "#64748B", fontSize: "11px" },
          },
          title: {
            text: t("dashboard.revenue"),
            style: {
              color: "#64748B",
              fontWeight: 600,
              fontSize: "12px",
            },
          },
        },
        grid: {
          strokeDashArray: 3,
          borderColor: "#EFE2EB",
          padding: { right: 20 },
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: (value) =>
              "฿" +
              value.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              }),
          },
        },
        colors: ["#E8307A"],
      },
    };
  }, [dashboardData, t]);

  return (
    <Card
      className="shadow-sm p-4 rounded-4"
      style={{ border: "1px solid #f1f1f1" }}
    >
      <h6 className="fw-bold mb-3">
        {t("dashboard.bookingPerformance14Days")}
      </h6>

      <div className="d-flex gap-3 mb-3 flex-wrap">
        <span style={{ fontSize: "14px" }}>
          {t("dashboard.totalBooked")}{" "}
          <strong style={{ color: "#E8307A" }}>{stats.totalBooked}</strong>
        </span>

        <span style={{ fontSize: "14px" }}>
          {t("dashboard.revenue")}{" "}
          <strong style={{ color: "#E8307A" }}>
            ฿{stats.totalRevenue.toLocaleString()}
          </strong>
        </span>

        <span style={{ fontSize: "14px" }}>
          {t("dashboard.adr")}{" "}
          <strong style={{ color: "#E8307A" }}>
            ฿{stats.adr.toLocaleString()}
          </strong>
        </span>
      </div>

      {series[0].data.length > 0 ? (
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={280}
        />
      ) : (
        <div className="text-center text-muted py-5">
          {t("common.noDataAvailable")}
        </div>
      )}
    </Card>
  );
};

export default BookingPerformance;
